import { QuantitySelectorUpdateEvent } from '@theme/events';
import { StandardEvents } from '@shopify/events';

/**
 * Bundle / quantity offer picker.
 * It only sets the quantity of the theme's own product form. Prices and discounts
 * are calculated by Shopify (variant price + automatic discounts in the cart).
 */
class MiniOfferPicker extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;
    this.addEventListener('change', this.#onChange, { signal });

    const section = this.closest('.shopify-section');
    section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // The product form may not be upgraded yet on first paint
    customElements.whenDefined('quantity-selector-component').then(() => this.#apply());
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get quantity() {
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="radio"]:checked'));
    return Math.max(1, parseInt(checked?.value ?? '1', 10) || 1);
  }

  #onChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;
    this.#apply();
  };

  #apply() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const quantity = String(this.quantity);
    const selector = /** @type {any} */ (section.querySelector('product-form-component quantity-selector-component'));
    const input = /** @type {HTMLInputElement | null} */ (
      section.querySelector('product-form-component input[name="quantity"]')
    );

    if (selector?.setValue) {
      selector.setValue(quantity);
    } else if (input) {
      input.value = quantity;
    } else {
      return;
    }

    (selector ?? input)?.dispatchEvent(new QuantitySelectorUpdateEvent(Number(quantity)));
  }

  #onProductSelect = async (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    try {
      const { detail } = await event.promise;
      const html = detail?.html;
      if (!html) return;

      const fresh = html.querySelector(`mini-offer-picker[data-block-id="${this.dataset.blockId}"]`);
      if (fresh) {
        // Swap in prices for the newly selected variant, keep the shopper's choice
        for (const price of this.querySelectorAll('[data-offer-price]')) {
          const key = price.getAttribute('data-offer-price');
          const next = fresh.querySelector(`[data-offer-price="${key}"]`);
          if (next) price.innerHTML = next.innerHTML;
        }
      }

      requestAnimationFrame(() => this.#apply());
    } catch (error) {
      // Aborted variant fetches are expected when shoppers click quickly
    }
  };
}

if (!customElements.get('mini-offer-picker')) customElements.define('mini-offer-picker', MiniOfferPicker);

/**
 * Click to play UGC videos (videos are not downloaded until played).
 */
class MiniVideo extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('[data-mini-play]');
    const video = this.querySelector('video');
    if (!button || !video) return;

    button.addEventListener('click', () => {
      for (const other of document.querySelectorAll('mini-video video')) {
        if (other !== video) /** @type {HTMLVideoElement} */ (other).pause();
      }
      button.hidden = true;
      video.controls = true;
      video.play().catch(() => {
        button.hidden = false;
      });
    });
  }
}

if (!customElements.get('mini-video')) customElements.define('mini-video', MiniVideo);
